import React, { useEffect, useState } from 'react';
import { Button, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, InputLabel } from '@mui/material';
import { ApiRequest } from 'GlobalFunctions';
import AutocompleteField from "elements/AutocompleteField";

export default function StoreLocationsEditor({ location, onClose, store_id }: any) {
    const [form, setForm] = useState({
        country_id: '',
        region_id: '',
        city_id: '',
        address: '',
        zip_code: ''
    });
    const [countries, setCountries] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        ApiRequest({
            method: 'GET',
            url: '/countries/getCountries',
            setResponse: (res: any) => setCountries(res)
        });
        if (location) {
            setForm({
                country_id: location.country_id || '',
                region_id: location.region_id || '',
                city_id: location.city_id || '',
                address: location.address || '',
                zip_code: location.zip_code || ''
            });
        }
    }, [location]);

    const handleCountryChange = (e: any) => {
        const countryId = e.target.value;
        setForm({
            ...form,
            country_id: countryId,
            region_id: '',
            city_id: ''
        });
        
        if (errors.country_id) {
            setErrors({
                ...errors,
                country_id: ''
            });
        }
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        
        // Validate form
        const newErrors: Record<string, string> = {};
        if (!form.country_id) newErrors.country_id = "Country is required";
        if (!form.region_id) newErrors.region_id = "State/Region is required";
        if (!form.city_id) newErrors.city_id = "City is required";
        if (!form.address) newErrors.address = "Address is required";
        if (!form.zip_code) newErrors.zip_code = "Zip Code is required";
        
        setErrors(newErrors);
        
        if (Object.keys(newErrors).length > 0) {
            return;
        }
        
        setLoading(true);
        const payload = {
            ...form,
            store_id,
            store_location_id: location ? location.store_location_id : undefined
        };
        await ApiRequest({
            method: 'POST',
            url: '/storesLocations/addedit',
            body: payload,
            setResponse: () => onClose(true)
        });
        setLoading(false);
    };

    return (
        <form onSubmit={handleSubmit}>
            <DialogTitle>{location ? 'Edit Location' : 'Add Location'}</DialogTitle>
            <DialogContent>
                <div className="textfield_wrapper">
                    <InputLabel className="external_label">Country</InputLabel>
                    <TextField
                        id="country_select"
                        value={form.country_id}
                        name="country_id"
                        onChange={handleCountryChange}
                        select
                        fullWidth
                        error={!!errors.country_id}
                        helperText={errors.country_id}
                    >
                        {countries.map((country: any, index: number) => (
                            <MenuItem key={index} value={country.country_id}>
                                {country.country_name}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                
                <div className="textfield_wrapper">
                    
                    <InputLabel className="external_label">State/Region</InputLabel>
                    <AutocompleteField
                        endpoint="/countries/getRegions"
                        endpoint_params={{ country_id: form.country_id }}
                        value={form.region_id}
                        className="autocomplete_small"
                        onChange={(data: any) => {
                            setForm({
                                ...form,
                                region_id: data ? data.value : '',
                                city_id: ''
                            });
                            
                            if (errors.region_id) {
                                setErrors({
                                    ...errors,
                                    region_id: ''
                                });
                            }
                        }}
                        fullWidth
                        error={!!errors.region_id}
                        helperText={errors.region_id}
                        disabled={(form.country_id === '' || form.country_id === '0')}
                    />
                </div>
                    
                {form.region_id !== '' && form.region_id !== '0' && (
                    <div className="textfield_wrapper">
                        <InputLabel className="external_label">City</InputLabel>
                        <AutocompleteField
                            endpoint="/shipping/zones/getCities"
                            endpoint_params={{ region_id: form.region_id }}
                            max_results={10}
                            dynamic={true}
                            value={form.city_id}
                            className="autocomplete_small"
                            onChange={(value: any) => {
                                setForm({
                                    ...form,
                                    city_id: value ? value.value : ''
                                });
                                
                                if (errors.city_id) {
                                    setErrors({
                                        ...errors,
                                        city_id: ''
                                    });
                                }
                            }}
                            clearOnSelect={true}
                            fullWidth
                            error={!!errors.city_id}
                            helperText={errors.city_id}
                        />
                    </div>
                )}
                
                <div className="textfield_wrapper">
                    <InputLabel className="external_label">Address</InputLabel>
                    <TextField
                        name="address"
                        value={form.address}
                        onChange={(e) => {
                            setForm({ ...form, address: e.target.value });
                            if (errors.address) {
                                setErrors({...errors, address: ''});
                            }
                        }}
                        fullWidth
                        error={!!errors.address}
                        helperText={errors.address}
                    />
                </div>
                
                <div className="textfield_wrapper">
                    <InputLabel className="external_label">Zip Code</InputLabel>
                    <TextField
                        name="zip_code"
                        value={form.zip_code}
                        onChange={(e) => {
                            setForm({ ...form, zip_code: e.target.value });
                            if (errors.zip_code) {
                                setErrors({...errors, zip_code: ''});
                            }
                        }}
                        fullWidth
                        error={!!errors.zip_code}
                        helperText={errors.zip_code}
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(false)} disabled={loading}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={loading}>{location ? 'Save' : 'Add'}</Button>
            </DialogActions>
        </form>
    );
}